import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { Profile } from '../../types/database';
import {
  Search,
  UserPlus,
  CreditCard as Edit2,
  Trash2,
  CheckCircle,
  XCircle,
  Key,
  Users as UsersIcon,
  FilterX,
} from 'lucide-react';
import { UserModal } from '../modals/UserModal';
import { Button } from '../ui/Button';
import { PageHeader } from '../ui/PageHeader';
import { Spinner } from '../ui/Spinner';
import { EmptyState } from '../ui/EmptyState';
import { Modal } from '../ui/Modal';
import { useConfirm } from '../ui/ConfirmDialog';
import { toast } from '../ui/Toaster';

export function UsersManagement() {
  const [users, setUsers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [sortBy,     setSortBy]     = useState<'newest' | 'oldest' | 'name'>('newest');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>();
  const [resetPasswordUserId, setResetPasswordUserId] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState('');
  const confirm = useConfirm();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('No se pudieron cargar los usuarios');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserStatus = async (user: Profile) => {
    const activating = !user.active;
    const ok = await confirm({
      title: activating ? 'Activar usuario' : 'Desactivar usuario',
      description: activating
        ? `¿Activar la cuenta de ${user.full_name || user.email}? Podrá iniciar sesión nuevamente.`
        : `¿Desactivar la cuenta de ${user.full_name || user.email}? No podrá iniciar sesión hasta reactivarlo.`,
      confirmText: activating ? 'Activar' : 'Desactivar',
      tone: activating ? 'info' : 'warning',
    });
    if (!ok) return;
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ active: activating })
        .eq('id', user.id);
      if (error) throw error;
      toast.success(activating ? 'Usuario activado' : 'Usuario desactivado');
      loadUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('No se pudo actualizar el estado');
    }
  };

  const handleDelete = async (user: Profile) => {
    const ok = await confirm({
      title: 'Eliminar usuario',
      description: `Se eliminará permanentemente a ${user.full_name || user.email}. Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session found');

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-delete-user`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: user.id }),
      });

      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'Error al eliminar usuario');

      toast.success('Usuario eliminado');
      loadUsers();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      toast.error(error.message || 'No se pudo eliminar el usuario');
    }
  };

  const handleEdit = (userId: string) => {
    setSelectedUserId(userId);
    setIsModalOpen(true);
  };

  const handleNew = () => {
    setSelectedUserId(undefined);
    setIsModalOpen(true);
  };

  const handleResetPassword = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!resetPasswordUserId || !newPassword) return;
    if (newPassword.length < 6) {
      setResetError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }

    setResetting(true);
    setResetError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session found');

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-reset-password`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: resetPasswordUserId, newPassword }),
      });

      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'Error al resetear contraseña');

      setResetPasswordUserId(null);
      setNewPassword('');
      toast.success('Contraseña reseteada');
    } catch (error: any) {
      console.error('Error resetting password:', error);
      setResetError(error.message || 'No se pudo resetear la contraseña');
    } finally {
      setResetting(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const search = searchTerm.toLowerCase();
    const filtered = users.filter((user) => {
      const matchesSearch =
        user.full_name?.toLowerCase().includes(search) ||
        user.email.toLowerCase().includes(search);
      const matchesRole = filterRole === 'all' || user.role === filterRole;
      return matchesSearch && matchesRole;
    });
    // Aplicar orden
    const collator = new Intl.Collator('es', { sensitivity: 'base' });
    if (sortBy === 'name') {
      filtered.sort((a, b) => collator.compare(a.full_name || '', b.full_name || ''));
    } else if (sortBy === 'oldest') {
      filtered.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    } else {
      // newest (default)
      filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }
    return filtered;
  }, [users, searchTerm, filterRole, sortBy]);

  const hasFilters = searchTerm.trim() !== '' || filterRole !== 'all';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const resetUser = users.find((u) => u.id === resetPasswordUserId);

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <PageHeader
        eyebrow="Administración"
        eyebrowIcon={UsersIcon}
        title="Gestión de usuarios"
        description="Administra cuentas, roles y accesos a la plataforma."
        actions={
          <Button variant="lime" onClick={handleNew} leftIcon={<UserPlus className="w-4 h-4" />}>
            Nuevo usuario
          </Button>
        }
      />

      <div className="glass-surface rounded-2xl p-6">
        <div className="flex flex-col sm:flex-row gap-3 mb-6" role="search">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-subtle pointer-events-none" aria-hidden="true" />
            <label htmlFor="users-search" className="sr-only">Buscar usuarios</label>
            <input
              id="users-search"
              type="search"
              placeholder="Buscar por nombre o email…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 glass-input rounded-lg text-white placeholder:text-faint"
            />
          </div>
          <label htmlFor="users-role" className="sr-only">Filtrar por rol</label>
          <select
            id="users-role"
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-4 py-2.5 glass-input rounded-lg text-white min-w-[180px]"
          >
            <option value="all">Todos los roles</option>
            <option value="admin">Administradores</option>
            <option value="docente">Docentes</option>
            <option value="alumno">Alumnos</option>
          </select>
          <label htmlFor="users-sort" className="sr-only">Ordenar por</label>
          <select
            id="users-sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'newest' | 'oldest' | 'name')}
            className="px-4 py-2.5 glass-input rounded-lg text-white min-w-[180px]"
            title="Ordenar lista"
          >
            <option value="newest">Más recientes primero</option>
            <option value="oldest">Más antiguos primero</option>
            <option value="name">Por nombre (A→Z)</option>
          </select>
        </div>

        {filteredUsers.length === 0 ? (
          <EmptyState
            icon={hasFilters ? FilterX : UsersIcon}
            title={hasFilters ? 'Ningún usuario coincide con el filtro' : 'Aún no hay usuarios'}
            description={
              hasFilters
                ? 'Prueba ajustando la búsqueda o quitando los filtros.'
                : 'Crea el primer usuario para comenzar.'
            }
            action={
              hasFilters ? (
                <Button variant="glass" size="sm" onClick={() => { setSearchTerm(''); setFilterRole('all'); }}>
                  Limpiar filtros
                </Button>
              ) : (
                <Button variant="lime" onClick={handleNew} leftIcon={<UserPlus className="w-4 h-4" />}>
                  Nuevo usuario
                </Button>
              )
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.08]">
                  <th scope="col" className="text-left py-3 px-4 text-xs uppercase tracking-wide text-subtle font-medium">Nombre</th>
                  <th scope="col" className="text-left py-3 px-4 text-xs uppercase tracking-wide text-subtle font-medium">Email</th>
                  <th scope="col" className="text-left py-3 px-4 text-xs uppercase tracking-wide text-subtle font-medium">Rol</th>
                  <th scope="col" className="text-left py-3 px-4 text-xs uppercase tracking-wide text-subtle font-medium">Estado</th>
                  <th scope="col" className="text-left py-3 px-4 text-xs uppercase tracking-wide text-subtle font-medium">Registrado</th>
                  <th scope="col" className="text-right py-3 px-4 text-xs uppercase tracking-wide text-subtle font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="border-b border-white/[0.06] hover:bg-white/[0.04] transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#E8FF30] to-[#C4E600] flex items-center justify-center text-cubus-bg font-bold text-sm shadow-[0_4px_12px_rgba(221,255,0,0.25)]">
                          {(user.full_name || user.email).charAt(0).toUpperCase()}
                        </div>
                        <span className="text-white font-medium">{user.full_name || 'Sin nombre'}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-muted">{user.email}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`chip ${
                          user.role === 'admin' ? 'chip-lime' : user.role === 'docente' ? 'chip-cyan' : 'chip-emerald'
                        } capitalize`}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <button
                        type="button"
                        onClick={() => toggleUserStatus(user)}
                        aria-pressed={user.active}
                        aria-label={user.active ? `Desactivar a ${user.full_name || user.email}` : `Activar a ${user.full_name || user.email}`}
                        className="inline-flex items-center gap-2 px-2 py-1 rounded-md hover:bg-white/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cubus-lime transition"
                      >
                        {user.active ? (
                          <CheckCircle className="w-4 h-4 text-emerald-400" aria-hidden="true" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-400" aria-hidden="true" />
                        )}
                        <span className={user.active ? 'text-emerald-400 text-sm' : 'text-red-400 text-sm'}>
                          {user.active ? 'Activo' : 'Inactivo'}
                        </span>
                      </button>
                    </td>
                    <td className="py-3 px-4 text-subtle text-sm whitespace-nowrap" title={new Date(user.created_at).toLocaleString('es-ES')}>
                      {new Date(user.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleEdit(user.id)}
                          className="p-2 text-blue-300 hover:bg-blue-500/10 rounded-lg transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cubus-lime"
                          aria-label={`Editar ${user.full_name || user.email}`}
                        >
                          <Edit2 className="w-4 h-4" aria-hidden="true" />
                        </button>
                        <button
                          onClick={() => {
                            setResetPasswordUserId(user.id);
                            setNewPassword('');
                            setResetError('');
                          }}
                          className="p-2 text-cubus-lime hover:bg-cubus-lime/10 rounded-lg transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cubus-lime"
                          aria-label={`Resetear contraseña de ${user.full_name || user.email}`}
                        >
                          <Key className="w-4 h-4" aria-hidden="true" />
                        </button>
                        <button
                          onClick={() => handleDelete(user)}
                          className="p-2 text-red-300 hover:bg-red-500/10 rounded-lg transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cubus-lime"
                          aria-label={`Eliminar ${user.full_name || user.email}`}
                        >
                          <Trash2 className="w-4 h-4" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { label: 'Total usuarios', value: users.length, tone: 'bg-cubus-lime/15 text-cubus-lime', icon: UserPlus },
          { label: 'Docentes', value: users.filter((u) => u.role === 'docente').length, tone: 'bg-cubus-cyan/15 text-cubus-cyan', icon: UserPlus },
          { label: 'Alumnos', value: users.filter((u) => u.role === 'alumno').length, tone: 'bg-emerald-400/15 text-emerald-400', icon: UserPlus },
        ].map((s) => (
          <div key={s.label} className="glass-surface rounded-2xl p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-subtle text-sm">{s.label}</h3>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.tone}`}>
                <s.icon className="w-5 h-5" aria-hidden="true" />
              </div>
            </div>
            <p className="text-3xl font-bold text-white text-display">{s.value}</p>
          </div>
        ))}
      </div>

      <UserModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedUserId(undefined);
        }}
        onSuccess={loadUsers}
        userId={selectedUserId}
      />

      <Modal
        open={!!resetPasswordUserId}
        onClose={() => {
          setResetPasswordUserId(null);
          setNewPassword('');
          setResetError('');
        }}
        title="Resetear contraseña"
        description={resetUser ? `Asignar nueva contraseña a ${resetUser.full_name || resetUser.email}.` : undefined}
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setResetPasswordUserId(null);
                setNewPassword('');
                setResetError('');
              }}
              disabled={resetting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              form="reset-password-form"
              variant="lime"
              loading={resetting}
              disabled={newPassword.length < 6}
            >
              Resetear contraseña
            </Button>
          </>
        }
      >
        <form id="reset-password-form" onSubmit={handleResetPassword} className="space-y-4">
          {resetError && (
            <div role="alert" className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">
              {resetError}
            </div>
          )}
          <div>
            <label htmlFor="new-user-password" className="block text-xs font-medium text-subtle mb-1.5 uppercase tracking-wide">
              Nueva contraseña
            </label>
            <input
              id="new-user-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Mínimo 6 caracteres"
              className="w-full px-4 py-2.5 glass-input rounded-lg text-white placeholder:text-faint"
              minLength={6}
              required
            />
            <p className="text-xs text-faint mt-1">El usuario podrá cambiar esta contraseña más tarde.</p>
          </div>
        </form>
      </Modal>
    </div>
  );
}
